// frontend/store/notifications.ts
//
// Pinia-стор для v2-инбокса уведомлений. Hybrid-модель из ADR:
//   * pending → viewed         — батчем при показе списка (markViewed)
//   * pending|viewed → acted   — клик по CTA (act)
//   * pending|viewed → dismissed — explicit (dismiss / bulkDismiss)
//   * expired                  — клон-кронжоб на бэке
//
// UX 5.2: единый хронологический список. Серверный фильтр живёт на
// /notifications/?category=&q= (БЕЗ ?tab= — без таба бэк отдаёт все статусы,
// отсортированные -created_at). state хранит currentCategory/currentSearch,
// любая смена фильтра обнуляет ленту + флаг loaded.
//
// Read-state — ИСКЛЮЧИТЕЛЬНО status-машина (pending → viewed →
// acted|dismissed|expired). Прежние legacy-aliases markRead/markAllRead и
// поле is_read удалены (Task #4, spec 5.6) — потребители используют
// markViewed/act/dismiss напрямую.

import { defineStore } from 'pinia';

import {
  fetchNotificationsApi,
  fetchNotificationsUnreadCountApi,
  markNotificationsViewedApi,
  actOnNotificationApi,
  dismissNotificationApi,
  bulkDismissNotificationsApi,
} from '@/services/api';

import type { RawNotification, PaginatedNotifications } from '@/shared/types';

// Re-export для consumers, которые тащили тип напрямую из стора.
// NotificationsPage.vue делает `import { useNotifications, type RawNotification }`,
// и до T24 нам нужно сохранить это рабочим.
export type { RawNotification, PaginatedNotifications } from '@/shared/types';

interface NotificationsState {
  /** Текущий буфер уведомлений (только то, что показано в дропдауне/странице). */
  items: RawNotification[];
  /** Точное кол-во непрочитанных (приходит с бэка отдельным endpoint). */
  unreadCount: number;
  /** URL следующей страницы (если сервер использует pagination). */
  next: string | null;
  /** Флаг «уже сделали первый load()». */
  loaded: boolean;
  /** Флаг параллельных запросов. */
  loading: boolean;
  /** Категория-фильтр (bookings/events/...), null = все категории. */
  currentCategory: string | null;
  /** Поисковая строка по title/body. */
  currentSearch: string;
}

const sortByNewest = (items: RawNotification[]): RawNotification[] =>
  [...items].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

// UX 5.5: terminal dismissed/expired must never render in the unified feed.
// The backend default (no-tab) list already excludes them; this is the
// store-side guard so a dismiss()ed card cannot return on the next load() and a
// stray terminal payload (e.g. a WS push) never resurfaces. `acted` stays (read).
const HIDDEN_STATUSES: ReadonlyArray<RawNotification['status']> = [
  'dismissed',
  'expired',
];

const isVisibleInFeed = (n: RawNotification): boolean =>
  !HIDDEN_STATUSES.includes(n.status);

// E04: колокол (NotificationComponent) привязывает AbortController к своему
// жизненному циклу и отменяет poll-запросы при unmount/логауте — это не
// ошибка, а штатная отмена, шуметь в консоль не нужно.
const isAbortError = (error: unknown): boolean => {
  const e = error as { code?: string; name?: string } | null | undefined;
  return e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';
};

/**
 * LG-023: ключ схлопывания уведомлений по одному обращению.
 *
 * По одному тикету в панели копились несколько карточек: автоподтверждение
 * «принято в работу» и реальный ответ приходили в одну минуту, и на один
 * повод пользователь видел два уведомления, причём первое («мы свяжемся с
 * вами в ближайшее время») выглядело нелепо рядом с уже пришедшим ответом.
 *
 * Схлопываем только тикеты: у брони и события своя логика, и там несколько
 * событий по одному объекту — это нормальный поток, а не дубль.
 */
const ticketKeyOf = (n: RawNotification): string | null => {
  const resource = n.resource;
  if (!resource || resource.type !== 'ticket' || !resource.id) return null;
  return `ticket:${resource.id}`;
};

const idOf = (n: RawNotification): string =>
  // public_id — канон в v2, id — legacy fallback на время раскатки T24-T25.
  n.public_id ?? (n.id ?? '');

const isPaginated = (data: unknown): data is PaginatedNotifications =>
  !!data && typeof data === 'object' && 'results' in (data as Record<string, unknown>);

// Мини-зеркало backend/PlatformaAPP/notifications/categories.py. WS-payload
// несёт только event_type (без category), а карточка красится по category.
// Выводим её из префикса event_type на клиенте — иначе realtime-уведомление
// мигает «other» (purple) до ближайшего reconcile/load.
const CATEGORY_BY_PREFIX: ReadonlyArray<[string, RawNotification['category']]> = [
  ['BOOKING_', 'bookings'],
  ['EVENT_', 'events'],
  ['MENTOR_APP_', 'mentor_app'],
  ['PAYMENT_', 'payments'],
  ['SUPPORT.', 'support'],
];

const categoryFromEventType = (eventType: string): RawNotification['category'] => {
  for (const [prefix, category] of CATEGORY_BY_PREFIX) {
    if (eventType.startsWith(prefix)) return category;
  }
  return 'other';
};

/**
 * Нормализация payload-а из WS-канала в RawNotification.
 *
 * WS (см. backend/PlatformaAPP/notifications/tasks.py::process_notification)
 * присылает СОКРАЩЁННЫЙ snapshot: только то, что нужно колоколу для отрисовки
 * и навигации. По сравнению с REST'ом ({@link fetchNotificationsApi})
 * отсутствуют `cta`, `category`, и `*_at`-timestamps. Это намеренно: дать
 * фронту минимум, чтобы:
 *   * показать заголовок и тело сразу,
 *   * иметь public_id для dedupe и для последующих API-вызовов,
 *   * не плодить shape-drift при изменении сериализатора.
 *
 * Полный shape подтянется при следующем открытии дропдауна (notifications.load()).
 *
 * Если payload не похож на bell-уведомление (например, чат-пинг с roomId), —
 * возвращаем null, чтобы caller мог отфильтровать.
 */
const normalizeSocketPayload = (
  payload: Record<string, unknown>,
): RawNotification | null => {
  const publicId = payload.public_id;
  const eventType = payload.event_type;
  if (typeof publicId !== 'string' || !publicId) return null;
  if (typeof eventType !== 'string' || !eventType) return null;

  const resourceType =
    typeof payload.resource_type === 'string' && payload.resource_type
      ? payload.resource_type
      : null;
  const resourceId =
    typeof payload.resource_id === 'string' && payload.resource_id
      ? payload.resource_id
      : '';

  return {
    public_id: publicId,
    event_type: eventType,
    kind: (payload.kind as RawNotification['kind']) ?? null,
    priority: (payload.priority as RawNotification['priority']) ?? 'normal',
    recipient_role:
      (payload.recipient_role as RawNotification['recipient_role']) ?? null,
    title: typeof payload.title === 'string' ? payload.title : '',
    body: typeof payload.body === 'string' ? payload.body : '',
    status:
      (payload.status as RawNotification['status']) ?? ('pending' as RawNotification['status']),
    // category WS не присылает — выводим из event_type (зеркало BE
    // categories.py), чтобы realtime-карточка сразу красилась верно
    // (mentor_app=teal, payments=green, …), а не мигала «other». cta WS тоже
    // не шлёт — подтянется при reconcile/load.
    category: categoryFromEventType(eventType),
    resource: resourceType ? { type: resourceType, id: resourceId } : null,
    cta: null,
    created_at:
      typeof payload.created_at === 'string'
        ? payload.created_at
        : new Date().toISOString(),
    viewed_at: null,
    acted_at: null,
    dismissed_at: null,
    expires_at: null,
    message: typeof payload.body === 'string' ? payload.body : '',
  };
};

export const useNotifications = defineStore('notifications', {
  state: (): NotificationsState => ({
    items: [],
    unreadCount: 0,
    next: null,
    loaded: false,
    loading: false,
    currentCategory: null,
    currentSearch: '',
  }),

  getters: {
    /**
     * Лента для показа: по одному обращению остаётся одно уведомление —
     * самое свежее, то есть отражающее текущий статус.
     *
     * ``items`` уже отсортирован по убыванию даты (``sortByNewest``), поэтому
     * первая встреченная карточка группы и есть актуальная. История не
     * теряется: полная последовательность статусов видна на странице тикета.
     */
    feedItems(state): RawNotification[] {
      const seenTickets = new Set<string>();
      return state.items.filter((n) => {
        const key = ticketKeyOf(n);
        if (!key) return true;
        if (seenTickets.has(key)) return false;
        seenTickets.add(key);
        return true;
      });
    },
  },

  actions: {
    /**
     * Загружает первую страницу единого списка через серверный фильтр
     * (category/q), либо догружает следующую страницу из `pageUrl`
     * (append-режим).
     *
     * UX 5.2: НЕ передаём `?tab=`. Без таба бэк (notifications/viewset.py
     * list()) отдаёт ВСЕ статусы (pending/viewed/acted/dismissed/expired),
     * уже отсортированные -created_at — это и есть единый хронологический
     * список. Деление action/history и FE-сплит action/info убраны.
     */
    async load(pageUrl: string | null = null, signal?: AbortSignal): Promise<void> {
      if (this.loading) return;
      this.loading = true;

      try {
        const params: Record<string, string> = {};
        if (this.currentCategory) params.category = this.currentCategory;
        if (this.currentSearch) params.q = this.currentSearch;

        const data = pageUrl
          ? await fetchNotificationsApi({ page: pageUrl }, signal)
          : await fetchNotificationsApi(params, signal);

        let nextUrl: string | null = null;
        let fresh: RawNotification[] = [];

        if (isPaginated(data)) {
          fresh = data.results;
          nextUrl = data.next ?? null;
        } else if (Array.isArray(data)) {
          fresh = data as RawNotification[];
          nextUrl = null;
        } else {
          console.warn('notifications.load(): unknown payload', data);
        }

        // UX 5.5: keep terminal dismissed/expired out of the unified feed even
        // if a payload slips through (the backend default list already excludes
        // them); this makes dismiss() removal stick across reloads.
        const visibleFresh = fresh.filter(isVisibleInFeed);
        this.items = pageUrl
          ? sortByNewest([...this.items, ...visibleFresh])
          : sortByNewest(visibleFresh);
        this.next = nextUrl;
        this.loaded = true;
      } catch (error) {
        if (!isAbortError(error)) console.error('Failed to load notifications', error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Принять real-time push с WS-канала /ws/notifications/.
     *
     * Возвращает true, если payload опознан как bell-уведомление и был
     * принят к показу (новое или подтверждение существующего), false —
     * если payload это что-то другое (например, чат-пинг с roomId).
     *
     * Dedupe по public_id:
     *   * первый раз — пушим в items, инкрементим unreadCount;
     *   * повтор (тот же public_id уже в ленте) — игнорируем как duplicate.
     * Это страхует от двойного «бам» при гонке WS-push и REST-polling.
     */
    pushFromSocket(payload: Record<string, unknown>): boolean {
      const incoming = normalizeSocketPayload(payload);
      if (!incoming) return false;
      // UX 5.5: never resurface a terminal (dismissed/expired) card via WS.
      if (!isVisibleInFeed(incoming)) return true;
      const existing = this.items.find(n => idOf(n) === incoming.public_id);
      if (existing) {
        return true; // recognised, but dedupe'd
      }
      this.items = sortByNewest([incoming, ...this.items]);
      // unreadCount считается только для pending. Если по какой-то причине
      // BE прислал уже viewed/acted (например, мы сами только что
      // подтвердили — гонка), счётчик не трогаем.
      if (incoming.status === 'pending') {
        this.unreadCount += 1;
      }
      return true;
    },

    /**
     * Точечный счётчик через выделенный endpoint (D-9).
     *
     * UX 5.1: счётчик и список — разные endpoint'ы и раньше расходились
     * (badge инкрементился, но `items` оставались стейл, т.к. `load()`
     * гейтится на `!loaded`). Здесь мы детектим РОСТ счётчика и помечаем
     * кэш списка устаревшим (`loaded = false`), чтобы следующий открытый
     * dropdown (watch(open) в NotificationComponent) гарантированно
     * перезагрузил ленту и показал новое уведомление БЕЗ перезагрузки
     * страницы. Уменьшение счётчика (мы сами что-то прочитали/дисмиссили)
     * кэш не инвалидирует — он уже консистентен после оптимистичной мутации.
     */
    async refreshUnreadCount(signal?: AbortSignal): Promise<void> {
      try {
        const { count } = await fetchNotificationsUnreadCountApi(signal);
        const next = typeof count === 'number' ? count : 0;
        if (next > this.unreadCount) {
          // На сервере появилось что-то новое — текущий буфер неполон.
          this.loaded = false;
        }
        this.unreadCount = next;
      } catch (error) {
        if (!isAbortError(error)) console.error('Failed to load unread_count', error);
      }
    },

    /**
     * UX 5.1: атомарно подтянуть и счётчик, и список из ОДНОГО источника.
     *
     * Используется фоновым polling'ом колокола, чтобы список реактивно
     * обновлялся вместе со счётчиком (а не только при открытии dropdown'а).
     * Порядок важен: сперва счётчик (он может выставить loaded=false при
     * росте), затем load() — он подтягивает уже консистентный список и
     * выставляет loaded=true, так что постусловие «буфер актуален» держится.
     */
    async refreshFromServer(signal?: AbortSignal): Promise<void> {
      await this.refreshUnreadCount(signal);
      await this.load(null, signal);
    },

    setCategory(category: string | null): void {
      if (this.currentCategory === category) return;
      this.currentCategory = category;
      this.items = [];
      this.next = null;
      this.loaded = false;
    },

    setSearch(q: string): void {
      const next = q ?? '';
      if (this.currentSearch === next) return;
      this.currentSearch = next;
      this.items = [];
      this.next = null;
      this.loaded = false;
    },

    /**
     * Батч pending → viewed.
     * Вызывается из IntersectionObserver-pипа после того,
     * как уведомление реально появилось в видимой области.
     */
    async markViewed(ids: string[]): Promise<void> {
      if (!ids.length) return;
      try {
        await markNotificationsViewedApi(ids);
      } catch (error) {
        console.error('Failed to mark notifications viewed', error);
        return;
      }
      const idSet = new Set(ids);
      const now = new Date().toISOString();
      this.items = this.items.map(n =>
        idSet.has(idOf(n)) && n.status === 'pending'
          ? { ...n, status: 'viewed', viewed_at: now }
          : n,
      );
      void this.refreshUnreadCount();
    },

    /** Клик по CTA → status = acted. */
    async act(publicId: string): Promise<void> {
      try {
        await actOnNotificationApi(publicId);
      } catch (error) {
        console.error('Failed to act on notification', error);
        return;
      }
      const now = new Date().toISOString();
      this.items = this.items.map(n =>
        idOf(n) === publicId
          ? { ...n, status: 'acted', acted_at: now }
          : n,
      );
      void this.refreshUnreadCount();
    },

    /**
     * Явное «убрать из инбокса» (крестик на карточке).
     *
     * В едином хронологическом списке (UX 5.5) dismiss — ЕДИНСТВЕННОЕ
     * удаление: viewed/acted остаются в ленте как «прочитанные», и только
     * dismiss физически убирает карточку. Поэтому здесь мы выкидываем item
     * из `items`, а не флипаем status in-place (иначе dismissed-карточка
     * осталась бы видимой в общем списке). Порядок остального не меняется —
     * filter сохраняет уже отсортированный буфер.
     */
    async dismiss(publicId: string): Promise<void> {
      try {
        await dismissNotificationApi(publicId);
      } catch (error) {
        console.error('Failed to dismiss notification', error);
        return;
      }
      this.items = this.items.filter(n => idOf(n) !== publicId);
      void this.refreshUnreadCount();
    },

    /**
     * Скрыть карточку вместе со всей её группой.
     *
     * Схлопнутая карточка представляет несколько уведомлений по одному
     * обращению. Если убрать только её, представителем группы станет
     * следующая по свежести — и на месте закрытой карточки тут же всплывёт
     * более старая. Поэтому группу убираем целиком.
     */
    async dismissGroup(publicId: string): Promise<void> {
      const target = this.items.find((n) => idOf(n) === publicId);
      const key = target ? ticketKeyOf(target) : null;
      if (!key) {
        await this.dismiss(publicId);
        return;
      }
      const ids = this.items
        .filter((n) => ticketKeyOf(n) === key)
        .map(idOf)
        .filter(Boolean);
      if (ids.length <= 1) {
        await this.dismiss(publicId);
        return;
      }
      await this.bulkDismiss({ ids });
    },

    /**
     * Массовый dismiss по ids или по filter.
     * Оптимистично выкидываем подходящие items из ленты:
     *   * если переданы ids — убираем именно их;
     *   * если переданы filter (kind/status) — убираем подходящие;
     *   * если ни ids ни filter (например, "очистить всё") — чистим всю ленту.
     */
    async bulkDismiss(params: {
      ids?: string[];
      filter?: { kind?: string; status?: string };
    } = {}): Promise<void> {
      try {
        await bulkDismissNotificationsApi(params);
      } catch (error) {
        console.error('Failed to bulk dismiss notifications', error);
        return;
      }
      const idSet = params.ids ? new Set(params.ids) : null;
      const { kind, status } = params.filter ?? {};
      this.items = this.items.filter(n => {
        if (idSet && idSet.has(idOf(n))) return false;
        if (kind || status) {
          const kindMatch = !kind || n.kind === kind;
          const statusMatch = !status || n.status === status;
          if (kindMatch && statusMatch) return false;
          return true;
        }
        // Без ids и без filter — bulk-dismiss всего, что в ленте.
        if (!idSet) return false;
        return true;
      });
      void this.refreshUnreadCount();
    },

    async refresh(): Promise<void> {
      await this.load();
    },

    clear(): void {
      this.$reset();
    },
  },
});
